import { create } from 'zustand';
import { Task, TaskFilters } from '../types/task.type';
import {
  getTasks,
  getMyTasks,
  createTask,
  updateTask,
  deleteTask,
  createMyTask,
  updateMyTask,
  deleteMyTask,
  syncTasksOnce,
} from '../services/task.service';
import {
  CreateEmployeeTaskRequest,
  CreateMyTaskRequest,
  UpdateEmployeeTaskRequest,
  UpdateMyTaskRequest,
} from '../types/task.type';
import { normalizeTask, sameTask } from '../utils/task.utils';

let orgTasksRequest: Promise<void> | null = null;
let orgTasksGeneration = 0;
let myTasksRequest: Promise<void> | null = null;
let myTasksGeneration = 0;

interface FetchOptions {
  force?: boolean;
}

interface TaskState {
  tasks: Task[];
  tasksFetchedAt: number | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  fetchErrors: string[];
  fetchTasks: (filters?: TaskFilters, options?: FetchOptions) => Promise<void>;
  syncAndRefreshTasks: (filters?: TaskFilters) => Promise<void>;
  upsertOrgTask: (task: Task) => void;
  removeOrgTask: (googleTaskId: string) => void;
  createEmployeeTask: (body: CreateEmployeeTaskRequest) => Promise<Task>;
  updateEmployeeTask: (googleTaskId: string, body: UpdateEmployeeTaskRequest) => Promise<Task>;
  deleteEmployeeTask: (task: Task) => Promise<void>;
  myTasks: Task[];
  myTasksFetchedAt: number | null;
  myTasksLoading: boolean;
  myTasksRefreshing: boolean;
  myTasksError: string | null;
  myTasksFetchErrors: string[];
  fetchMyTasks: (params?: Pick<TaskFilters, 'task_list_id'>, options?: FetchOptions) => Promise<void>;
  syncAndRefreshMyTasks: (params?: Pick<TaskFilters, 'task_list_id'>) => Promise<void>;
  upsertMyTask: (task: Task) => void;
  removeMyTask: (googleTaskId: string) => void;
  createManagerTask: (body: CreateMyTaskRequest) => Promise<Task>;
  updateManagerTask: (googleTaskId: string, body: UpdateMyTaskRequest) => Promise<Task>;
  deleteManagerTask: (task: Task) => Promise<void>;
}

export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  tasksFetchedAt: null,
  isLoading: false,
  isRefreshing: false,
  error: null,
  fetchErrors: [],
  myTasks: [],
  myTasksFetchedAt: null,
  myTasksLoading: false,
  myTasksRefreshing: false,
  myTasksError: null,
  myTasksFetchErrors: [],

  fetchTasks: (filters: TaskFilters = {}, options: FetchOptions = {}) => {
    if (orgTasksRequest && !options.force) return orgTasksRequest;
    const generation = ++orgTasksGeneration;
    const isInitial = get().tasks.length === 0;
    set({
      isLoading: isInitial,
      isRefreshing: !isInitial,
      error: null,
      fetchErrors: [],
    });

    const request = (async () => {
      try {
        const params: Record<string, string> = {};
        if (filters.employee_id) params.employee_id = filters.employee_id;
        if (filters.task_list_id) params.task_list_id = filters.task_list_id;
        const res = await getTasks(params);
        if (generation !== orgTasksGeneration) return;
        set({
          tasks: (res.data ?? []).map(normalizeTask),
          tasksFetchedAt: Date.now(),
          isLoading: false,
          isRefreshing: false,
          fetchErrors: res.errors || [],
        });
      } catch (e: any) {
        if (generation !== orgTasksGeneration) return;
        set({
          error: e.message || 'Failed to load tasks',
          isLoading: false,
          isRefreshing: false,
        });
      } finally {
        if (generation === orgTasksGeneration) orgTasksRequest = null;
      }
    })();

    orgTasksRequest = request;
    return request;
  },

  syncAndRefreshTasks: async (filters: TaskFilters = {}) => {
    set({ isRefreshing: true, error: null });
    try {
      await syncTasksOnce();
      await get().fetchTasks(filters, { force: true });
    } catch (e: any) {
      set({ isRefreshing: false });
      throw e;
    }
  },

  upsertOrgTask: (task: Task) => {
    const next = normalizeTask(task);
    set(state => {
      const idx = state.tasks.findIndex(t => sameTask(t, next));
      const tasks = idx >= 0
        ? state.tasks.map((t, i) => (i === idx ? next : t))
        : [next, ...state.tasks];
      return { tasks, tasksFetchedAt: Date.now() };
    });
  },

  removeOrgTask: (googleTaskId: string) => {
    set(state => ({
      tasks: state.tasks.filter(t => t.google_task_id !== googleTaskId),
      tasksFetchedAt: Date.now(),
    }));
  },

  createEmployeeTask: async (body) => {
    const task = await createTask(body);
    get().upsertOrgTask(task);
    return task;
  },

  updateEmployeeTask: async (googleTaskId, body) => {
    const task = normalizeTask(await updateTask(googleTaskId, body));
    get().upsertOrgTask(task);
    return task;
  },

  deleteEmployeeTask: async (task) => {
    await deleteTask(task);
    get().removeOrgTask(task.google_task_id);
  },

  fetchMyTasks: (params: Pick<TaskFilters, 'task_list_id'> = {}, options: FetchOptions = {}) => {
    if (myTasksRequest && !options.force) return myTasksRequest;
    const generation = ++myTasksGeneration;
    const state = get();
    const isInitial = state.myTasks.length === 0;
    set({
      myTasksLoading: isInitial,
      myTasksRefreshing: !isInitial,
      myTasksError: null,
      myTasksFetchErrors: [],
    });

    const request = (async () => {
      try {
        const query: Record<string, string> = {};
        if (params.task_list_id) query.task_list_id = params.task_list_id;
        const res = await getMyTasks(query);
        if (generation !== myTasksGeneration) return;
        set({
          myTasks: (res.data ?? []).map(normalizeTask),
          myTasksFetchedAt: Date.now(),
          myTasksLoading: false,
          myTasksRefreshing: false,
          myTasksFetchErrors: res.errors || [],
        });
      } catch (e: any) {
        if (generation !== myTasksGeneration) return;
        const detail = e.response?.data?.detail || e.message || 'Failed to load tasks';
        set({
          myTasksError: detail,
          myTasksLoading: false,
          myTasksRefreshing: false,
          myTasks: isInitial ? [] : state.myTasks,
        });
      } finally {
        if (generation === myTasksGeneration) myTasksRequest = null;
      }
    })();

    myTasksRequest = request;
    return request;
  },

  syncAndRefreshMyTasks: async (params: Pick<TaskFilters, 'task_list_id'> = {}) => {
    set({ myTasksRefreshing: true, myTasksError: null });
    try {
      await syncTasksOnce();
      await get().fetchMyTasks(params, { force: true });
    } catch (e) {
      set({ myTasksRefreshing: false });
      throw e;
    }
  },

  upsertMyTask: (task: Task) => {
    const next = normalizeTask(task);
    set(state => {
      const idx = state.myTasks.findIndex(t => sameTask(t, next));
      const myTasks = idx >= 0
        ? state.myTasks.map((t, i) => (i === idx ? next : t))
        : [next, ...state.myTasks];
      return { myTasks, myTasksFetchedAt: Date.now() };
    });
  },

  removeMyTask: (googleTaskId: string) => {
    set(state => ({
      myTasks: state.myTasks.filter(t => t.google_task_id !== googleTaskId),
      myTasksFetchedAt: Date.now(),
    }));
  },

  createManagerTask: async (body) => {
    const task = await createMyTask(body);
    get().upsertMyTask(task);
    return task;
  },

  updateManagerTask: async (googleTaskId, body) => {
    const task = normalizeTask(await updateMyTask(googleTaskId, body));
    get().upsertMyTask(task);
    return task;
  },

  deleteManagerTask: async (task) => {
    await deleteMyTask(task);
    get().removeMyTask(task.google_task_id);
  },
}));
